'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkClass =
    'text-sm font-medium text-[#b5b5b5] hover:text-[#f5c842] transition-colors';

  const mobileLinkClass =
    'text-sm font-medium text-[#b5b5b5] hover:text-[#f5c842] transition-colors';

  const earlyAccessButtonClass =
    'rounded-xl bg-[#f5c842] px-5 py-2.5 text-sm font-semibold text-black shadow-[0_0_18px_rgba(245,200,66,0.35)] transition-all duration-300 hover:bg-[#ffd65a] hover:shadow-[0_0_28px_rgba(245,200,66,0.55)] animate-pulse';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'border-b border-[#1a1a1a] bg-[#0a0a0a]/90 backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-6 lg:px-10">
          <Link href="/landing-page" className="flex items-center group shrink-0">
            <AppLogo
              src="/styleai/fhitz-wordmark.png"
              size={34}
              width={132}
              alt="FHITZ logo"
              imageClassName="h-[34px] w-auto object-contain"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <a href="#how-it-works" className={navLinkClass}>
              How it works
            </a>
            <a href="#samples" className={navLinkClass}>
              Examples
            </a>
            <a href="#features" className={navLinkClass}>
              Features
            </a>
            <a href="#pricing" className={navLinkClass}>
              Pricing
            </a>
          </div>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              href="/early-access"
              className="px-4 py-2 text-sm font-medium text-[#b5b5b5] hover:text-[#f5c842] transition-colors"
            >
              Sign in
            </Link>

            <Link href="/early-access" className={earlyAccessButtonClass}>
              Get Early Access
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#b5b5b5] hover:text-[#f5c842] transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Open mobile menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />

          <div className="absolute top-0 right-0 bottom-0 w-72 border-l border-[#1f1f1f] bg-[#111] p-6">
            <div className="mb-8 flex items-center justify-between">
              <AppLogo
                src="/styleai/fhitz-wordmark.png"
                size={32}
                width={124}
                alt="FHITZ logo"
                imageClassName="h-[32px] w-auto object-contain"
              />

              <button
                className="text-[#b5b5b5] hover:text-[#f5c842] transition-colors"
                onClick={() => setMobileOpen(false)}
                aria-label="Close mobile menu"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <a
                href="#how-it-works"
                className={mobileLinkClass}
                onClick={() => setMobileOpen(false)}
              >
                How it works
              </a>

              <a
                href="#samples"
                className={mobileLinkClass}
                onClick={() => setMobileOpen(false)}
              >
                Examples
              </a>

              <a
                href="#features"
                className={mobileLinkClass}
                onClick={() => setMobileOpen(false)}
              >
                Features
              </a>

              <a
                href="#pricing"
                className={mobileLinkClass}
                onClick={() => setMobileOpen(false)}
              >
                Pricing
              </a>

              <div className="mt-4 border-t border-[#1f1f1f] pt-4 flex flex-col gap-3">
                <Link
                  href="/early-access"
                  className={mobileLinkClass}
                  onClick={() => setMobileOpen(false)}
                >
                  Sign in
                </Link>

                <Link
                  href="/early-access"
                  className={`${earlyAccessButtonClass} text-center`}
                  onClick={() => setMobileOpen(false)}
                >
                  Get Early Access
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}